class BBShowMoreOptionValues extends HTMLElement {
  constructor() {
    super();
    this.maxVisibleOptions = 5;
    this.showMoreBtn = null;
    this.showMoreInput = null;
    this.optionElements = [];
    this.isExpanded = false;
    this.storageKey = null;
    this.boundEventHandlers = new Map();
  }

  connectedCallback() {
    // Wait for DOM to be fully constructed
    requestAnimationFrame(() => {
      this.init();
    });

    // Setup page unload handler
    this.setupPageUnloadHandler();
  }

  disconnectedCallback() {
    this.removeEventListeners();
  }
  init() {
    try {
      this.createStorageKey();
      this.findElements();
      this.restoreExpandedState();
      this.checkAutoExpansion();

      if (!this.shouldShowButton()) {
        this.hideButton();
        return;
      }

      this.showButton();
      this.setupEventListeners();
      this.updateVisibility();
    } catch (error) {
      console.error('BBShowMoreOptionValues: Error during initialization:', error);
    }
  }

  createStorageKey() {
    const sectionId = this.closest('[data-section-id]')?.dataset.sectionId || 'unknown';
    const productHandle = this.closest('variant-picker')?.getAttribute('handle') || 'unknown';
    const optionPosition = Array.from(this.closest('variant-picker')?.querySelectorAll('.variant-picker__option') || []).indexOf(this.closest('.variant-picker__option')) + 1;
    this.storageKey = `bb-show-more-${sectionId}-${productHandle}-option-${optionPosition}`;
  }

  findElements() {
    // Get all option value label elements
    this.optionElements = Array.from(this.children).filter((child) => child.tagName.toLowerCase() === 'label' && !child.classList.contains('show-more-btn'));

    this.showMoreBtn = this.querySelector('.show-more-btn');
    this.showMoreInput = this.querySelector('.show-more-input');
  }

  restoreExpandedState() {
    try {
      this.isExpanded = this.getStorageValue(this.storageKey) === 'true';
    } catch (error) {
      console.warn('BBShowMoreOptionValues: Failed to restore state from localStorage:', error);
      this.isExpanded = false;
    }
  }

  shouldShowButton() {
    return this.showMoreBtn && this.optionElements.length > this.maxVisibleOptions;
  }

  hideButton() {
    this.toggleElementDisplay(this.showMoreBtn, false);
    this.toggleElementDisplay(this.showMoreInput, false);
  }

  showButton() {
    this.toggleElementDisplay(this.showMoreBtn, true);
    this.toggleElementDisplay(this.showMoreInput, true);
  }

  toggleElementDisplay(element, show) {
    if (element) {
      element.style.display = show ? '' : 'none';
    }
  }
  setupEventListeners() {
    const handleButtonClick = (e) => {
      e.preventDefault();
      this.toggleExpanded();
    };

    const handleInputChange = (e) => {
      e.preventDefault();
      // Uncheck the checkbox immediately to prevent it from staying checked
      this.showMoreInput.checked = false;
      this.toggleExpanded();
    };

    if (this.showMoreBtn) {
      this.showMoreBtn.addEventListener('click', handleButtonClick);
      this.boundEventHandlers.set('buttonClick', handleButtonClick);
    }

    if (this.showMoreInput) {
      this.showMoreInput.addEventListener('change', handleInputChange);
      this.boundEventHandlers.set('inputChange', handleInputChange);
    }
  }

  removeEventListeners() {
    if (this.showMoreBtn && this.boundEventHandlers.has('buttonClick')) {
      this.showMoreBtn.removeEventListener('click', this.boundEventHandlers.get('buttonClick'));
    }

    if (this.showMoreInput && this.boundEventHandlers.has('inputChange')) {
      this.showMoreInput.removeEventListener('change', this.boundEventHandlers.get('inputChange'));
    }

    this.boundEventHandlers.clear();
  }
  toggleExpanded() {
    this.isExpanded = !this.isExpanded;
    this.saveExpandedState();
    this.updateVisibility();
  }

  saveExpandedState() {
    try {
      if (this.storageKey) {
        this.setStorageValue(this.storageKey, this.isExpanded.toString());
      }
    } catch (error) {
      console.warn('BBShowMoreOptionValues: Failed to save state to localStorage:', error);
    }
  }
  updateVisibility() {
    if (this.optionElements.length <= this.maxVisibleOptions) {
      return;
    }

    const remainingCount = this.optionElements.length - this.maxVisibleOptions;

    // Update visibility for hidden elements
    this.optionElements.forEach((labelElement, index) => {
      if (index >= this.maxVisibleOptions) {
        this.toggleOptionVisibility(labelElement, this.isExpanded);
      }
    });

    // Update button state and text
    this.updateButtonState(remainingCount);

    // Position button after 5th visible option when collapsed
    this.positionButton();
  }

  toggleOptionVisibility(labelElement, show) {
    const inputElement = labelElement.querySelector('input') || labelElement.previousElementSibling;

    labelElement.style.display = show ? '' : 'none';
    if (inputElement && inputElement.tagName.toLowerCase() === 'input') {
      inputElement.style.display = show ? '' : 'none';
    }
  }

  updateButtonState(remainingCount) {
    if (!this.isExpanded) {
      const buttonText = this.showMoreBtn?.querySelector('span');
      if (buttonText) {
        buttonText.textContent = `+${remainingCount}`;
      }
      this.showButton();
    } else {
      this.hideButton();
    }
  }

  positionButton() {
    if (!this.isExpanded && this.optionElements.length > this.maxVisibleOptions && this.showMoreBtn) {
      const fifthOption = this.optionElements[this.maxVisibleOptions - 1];
      if (fifthOption && fifthOption.nextElementSibling !== this.showMoreBtn) {
        fifthOption.insertAdjacentElement('afterend', this.showMoreBtn);
      }
    }
  }
  setupPageUnloadHandler() {
    // Only set up handlers once per page
    if (!window.bbShowMoreUnloadHandlerSet) {
      window.bbShowMoreUnloadHandlerSet = true;

      const sessionKey = 'bb-show-more-session';

      try {
        const currentSession = sessionStorage.getItem(sessionKey);

        if (!currentSession) {
          // New session, clear any old localStorage entries
          this.clearAllShowMoreStorage();
          sessionStorage.setItem(sessionKey, Date.now().toString());
        }

        // Clear localStorage when the tab is closed or navigated away from
        window.addEventListener('beforeunload', () => {
          try {
            sessionStorage.removeItem(sessionKey);
          } catch (error) {
            console.warn('BBShowMoreOptionValues: Failed to clear session marker:', error);
          }
        });
      } catch (error) {
        console.warn('BBShowMoreOptionValues: Failed to setup page unload handler:', error);
      }
    }
  }

  clearAllShowMoreStorage() {
    try {
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith('bb-show-more-')) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.warn('BBShowMoreOptionValues: Failed to clear localStorage:', error);
    }
  }
  checkAutoExpansion() {
    // Check if any selected option values are in the hidden section
    const hasSelectedHiddenOption = this.optionElements.some((labelElement, index) => {
      if (index >= this.maxVisibleOptions) {
        const inputElement = this.findInputElement(labelElement);
        return inputElement && inputElement.checked;
      }
      return false;
    });

    if (hasSelectedHiddenOption) {
      this.isExpanded = true;
      this.saveExpandedState();
    }
  }

  findInputElement(labelElement) {
    return labelElement.querySelector('input') || labelElement.previousElementSibling;
  }

  // Storage utilities with error handling
  getStorageValue(key) {
    try {
      return localStorage.getItem(key);
    } catch (error) {
      console.warn('BBShowMoreOptionValues: localStorage access failed:', error);
      return null;
    }
  }

  setStorageValue(key, value) {
    try {
      localStorage.setItem(key, value);
    } catch (error) {
      console.warn('BBShowMoreOptionValues: localStorage write failed:', error);
    }
  }

  clearAllShowMoreStorage() {
    try {
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith('bb-show-more-')) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.warn('BBShowMoreOptionValues: Failed to clear localStorage:', error);
    }
  }
}
customElements.define('bb-show-more-option-values', BBShowMoreOptionValues);

class BBInlineSearch extends HTMLElement {
  constructor() {
    super();
    this.isMenuOpen = false;
    this.searchInput = null;
    this.megaMenu = null;
    this.searchSubmit = null;
    this.form = null;
    this.predictiveSearch = null;
    this.mutationObserver = null;
    this.boundEventHandlers = new Map();

    // Bind methods once to avoid memory leaks
    this.handleClickOutside = this.handleClickOutside.bind(this);
  }

  connectedCallback() {
    try {
      this.findElements();

      if (!this.searchInput || !this.megaMenu) {
        console.warn('BBInlineSearch: Required elements not found');
        return;
      }

      this.setupEventListeners();
    } catch (error) {
      console.error('BBInlineSearch: Error during initialization:', error);
    }
  }

  disconnectedCallback() {
    this.removeEventListeners();
    this.cleanupObserver();
  }

  findElements() {
    this.searchInput = this.querySelector('input[name="q"]');
    this.megaMenu = this.querySelector('#bb-inline-search-mega-menu');
    this.searchSubmit = this.querySelector('[data-search-submit]');
    this.form = this.querySelector('form');
    this.predictiveSearch = this.querySelector('predictive-search');
  }
  setupEventListeners() {
    const handleInputChange = this.handleInputChange.bind(this);
    const handleInputFocus = this.handleInputFocus.bind(this);
    const handleMegaMenuClick = this.handleMegaMenuClick.bind(this);
    const handleSearchSubmit = this.handleSearchSubmit.bind(this);

    // Store bound handlers for cleanup
    this.boundEventHandlers.set('inputChange', handleInputChange);
    this.boundEventHandlers.set('inputFocus', handleInputFocus);
    this.boundEventHandlers.set('megaMenuClick', handleMegaMenuClick);
    this.boundEventHandlers.set('searchSubmit', handleSearchSubmit);

    // Listen for input changes and focus
    this.searchInput.addEventListener('input', handleInputChange);
    this.searchInput.addEventListener('focus', handleInputFocus);

    // Listen for clicks on the mega menu to prevent closing
    this.megaMenu.addEventListener('click', handleMegaMenuClick);

    // Escape schließt das Overlay
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isMenuOpen) {
        this.closeMegaMenu();
        this.searchInput.blur();
      }
    });

    // Handle search submit button
    if (this.searchSubmit) {
      this.searchSubmit.addEventListener('click', handleSearchSubmit);
    }

    // Listen for predictive search changes
    this.setupPredictiveSearchObserver();
  }

  setupPredictiveSearchObserver() {
    if (this.predictiveSearch) {
      this.mutationObserver = new MutationObserver(this.handlePredictiveSearchChange.bind(this));
      this.mutationObserver.observe(this.predictiveSearch, {
        childList: true,
        subtree: true,
        attributes: true,
      });
    }
  }

  cleanupObserver() {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }
  }
  removeEventListeners() {
    if (this.searchInput) {
      const inputChangeHandler = this.boundEventHandlers.get('inputChange');
      const inputFocusHandler = this.boundEventHandlers.get('inputFocus');

      if (inputChangeHandler) {
        this.searchInput.removeEventListener('input', inputChangeHandler);
      }
      if (inputFocusHandler) {
        this.searchInput.removeEventListener('focus', inputFocusHandler);
      }
    }

    if (this.megaMenu) {
      const megaMenuClickHandler = this.boundEventHandlers.get('megaMenuClick');
      if (megaMenuClickHandler) {
        this.megaMenu.removeEventListener('click', megaMenuClickHandler);
      }
    }

    if (this.searchSubmit) {
      const searchSubmitHandler = this.boundEventHandlers.get('searchSubmit');
      if (searchSubmitHandler) {
        this.searchSubmit.removeEventListener('click', searchSubmitHandler);
      }
    }

    document.removeEventListener('click', this.handleClickOutside);
    this.boundEventHandlers.clear();
  }
  // Redesign 2026 (snocks): Overlay öffnet schon beim Antippen und bleibt beim Leeren offen –
  // leeres Feld zeigt den gefüllten Leerzustand (snippets/tc-search-suggestions.liquid)
  handleInputChange() {
    this.openMegaMenu();
  }

  handleInputFocus() {
    this.openMegaMenu();
  }

  handleMegaMenuClick(event) {
    // Prevent closing when clicking inside the mega menu
    event.stopPropagation();
  }

  handleSearchSubmit(event) {
    event.preventDefault();
    const searchValue = this.searchInput.value.trim();

    if (this.form && searchValue !== '') {
      this.form.submit();
    }
  }

  handlePredictiveSearchChange() {
    const inputValue = this.searchInput.value.trim();

    if (inputValue.length > 0 && this.predictiveSearch) {
      const hasResults = this.predictiveSearch.querySelector('[slot="results"]')?.children.length > 0;
      const hasIdle = this.predictiveSearch.querySelector('[slot="idle"]')?.children.length > 0;

      if (hasResults || hasIdle) {
        this.openMegaMenu();
      }
    }
  }

  handleClickOutside(event) {
    // Close mega menu if clicking outside the search input or mega menu
    if (!this.contains(event.target)) {
      this.closeMegaMenu();
    }
  }

  toggleMegaMenu(shouldOpen) {
    if (shouldOpen) {
      this.openMegaMenu();
    } else {
      this.closeMegaMenu();
    }
  }
  openMegaMenu() {
    if (this.isMenuOpen) return;

    this.isMenuOpen = true;

    // Show the mega menu using CSS class
    this.megaMenu.classList.add('is-open');
    this.megaMenu.setAttribute('open', '');

    // Add click outside listener
    document.addEventListener('click', this.handleClickOutside);

    // Trigger predictive search if needed
    this.triggerPredictiveSearch();

    // Dispatch custom event for header transparency check
    this.dispatchCustomEvent('bb:header-check-transparency');
  }

  closeMegaMenu() {
    if (!this.isMenuOpen) return;

    this.isMenuOpen = false;

    // Hide the mega menu using CSS class
    this.megaMenu.classList.remove('is-open');
    this.megaMenu.removeAttribute('open');

    // Remove click outside listener
    document.removeEventListener('click', this.handleClickOutside);

    // Dispatch custom event for header transparency check
    this.dispatchCustomEvent('bb:header-check-transparency');
  }

  triggerPredictiveSearch() {
    if (this.predictiveSearch && this.searchInput) {
      try {
        const inputEvent = new Event('input', { bubbles: true });
        this.searchInput.dispatchEvent(inputEvent);
      } catch (error) {
        console.warn('BBInlineSearch: Failed to trigger predictive search:', error);
      }
    }
  }

  dispatchCustomEvent(eventName) {
    try {
      document.dispatchEvent(new CustomEvent(eventName));
    } catch (error) {
      console.warn(`BBInlineSearch: Failed to dispatch ${eventName} event:`, error);
    }
  }
}

customElements.define('bb-inline-search', BBInlineSearch);

class BBDeliveryTime extends HTMLElement {
  constructor() {
    super();
    this.minDays = 2;
    this.maxDays = 5;
    this.thresholdHour = 14;
    this.thresholdMinute = 0;
    this.localeText = '';
    this.fallbackText = '';
    this.hourSingular = 'hour';
    this.hourPlural = 'hours';
    this.minuteSingular = 'minute';
    this.minutePlural = 'minutes';
    this.andConnector = 'and';
    this.updateInterval = null;
    this.timeZone = 'Europe/Berlin';
  }
  connectedCallback() {
    try {
      // Block is already hidden via inline styles in HTML
      // Only show it after successful calculation

      this.parseAttributes();
      this.initializeDisplay();
      this.startUpdateInterval();
    } catch (error) {
      console.error('BBDeliveryTime: Error during initialization:', error);
      this.showFallbackDisplay();
    }
  }

  disconnectedCallback() {
    this.clearUpdateInterval();
  }
  // Add method to show fallback display when initialization fails
  showFallbackDisplay() {
    const proseDiv = this.querySelector('.prose p');
    if (proseDiv) {
      proseDiv.innerHTML = this.fallbackText || 'Delivery in 2-5 business days';
    }
    // Show the block after setting fallback content
    this.showBlock();
  }

  // Method to show the block with smooth transition
  showBlock() {
    this.style.visibility = 'visible';
    this.style.opacity = '1';
    this.style.transition = 'opacity 0.3s ease-in-out';
  }
  parseAttributes() {
    // Default fallback values
    const defaultMinDays = 2;
    const defaultMaxDays = 5;
    const defaultThresholdHour = 14;
    const defaultThresholdMinute = 0;

    // Parse and validate delivery days
    const rawMinDays = parseInt(this.getAttribute('data-min-days'));
    const rawMaxDays = parseInt(this.getAttribute('data-max-days'));

    if (isNaN(rawMinDays) || rawMinDays < 1 || rawMinDays > 30) {
      console.error('BBDeliveryTime: Invalid min delivery days value. Using fallback value of', defaultMinDays);
      this.minDays = defaultMinDays;
    } else {
      this.minDays = rawMinDays;
    }

    if (isNaN(rawMaxDays) || rawMaxDays < 1 || rawMaxDays > 30) {
      console.error('BBDeliveryTime: Invalid max delivery days value. Using fallback value of', defaultMaxDays);
      this.maxDays = defaultMaxDays;
    } else {
      this.maxDays = rawMaxDays;
    }

    // Ensure min days is not greater than max days
    if (this.minDays > this.maxDays) {
      console.error('BBDeliveryTime: Min delivery days cannot be greater than max delivery days. Using fallback values.');
      this.minDays = defaultMinDays;
      this.maxDays = defaultMaxDays;
    }

    // Parse and validate threshold hour
    const rawThresholdHour = parseInt(this.getAttribute('data-threshold-hour'));
    if (isNaN(rawThresholdHour) || rawThresholdHour < 0 || rawThresholdHour > 23) {
      console.error('BBDeliveryTime: Invalid threshold hour value (must be 0-23). Using fallback value of', defaultThresholdHour);
      this.thresholdHour = defaultThresholdHour;
    } else {
      this.thresholdHour = rawThresholdHour;
    }

    // Parse and validate threshold minute
    const rawThresholdMinute = parseInt(this.getAttribute('data-threshold-minute'));
    if (isNaN(rawThresholdMinute) || rawThresholdMinute < 0 || rawThresholdMinute > 59) {
      console.error('BBDeliveryTime: Invalid threshold minute value (must be 0-59). Using fallback value of', defaultThresholdMinute);
      this.thresholdMinute = defaultThresholdMinute;
    } else {
      this.thresholdMinute = rawThresholdMinute;
    }

    // Parse locale texts with validation
    this.localeText =
      this.getAttribute('data-locale-text') ||
      'Order within the next <strong>{{ remaining_time }}</strong> and receive delivery between <strong>{{ min_date }} and {{ max_date }}</strong>';
    this.fallbackText = this.getAttribute('data-fallback-text') || 'Delivery in 2-5 business days';
    this.hourSingular = this.getAttribute('data-hour-singular') || 'hour';
    this.hourPlural = this.getAttribute('data-hour-plural') || 'hours';
    this.minuteSingular = this.getAttribute('data-minute-singular') || 'minute';
    this.minutePlural = this.getAttribute('data-minute-plural') || 'minutes';
    this.andConnector = this.getAttribute('data-and-connector') || 'and';

    // Validate that required placeholders exist in locale text
    if (!this.localeText.includes('{{ remaining_time }}') || !this.localeText.includes('{{ min_date }}') || !this.localeText.includes('{{ max_date }}')) {
      console.error('BBDeliveryTime: Locale text is missing required placeholders. Using fallback text.');
      this.localeText = 'Order within the next <strong>{{ remaining_time }}</strong> and receive delivery between <strong>{{ min_date }} and {{ max_date }}</strong>';
    }

    // Validate that locale strings are not empty
    if (!this.hourSingular.trim()) {
      console.error('BBDeliveryTime: Hour singular text is empty. Using fallback.');
      this.hourSingular = 'hour';
    }
    if (!this.hourPlural.trim()) {
      console.error('BBDeliveryTime: Hour plural text is empty. Using fallback.');
      this.hourPlural = 'hours';
    }
    if (!this.minuteSingular.trim()) {
      console.error('BBDeliveryTime: Minute singular text is empty. Using fallback.');
      this.minuteSingular = 'minute';
    }
    if (!this.minutePlural.trim()) {
      console.error('BBDeliveryTime: Minute plural text is empty. Using fallback.');
      this.minutePlural = 'minutes';
    }
    if (!this.andConnector.trim()) {
      console.error('BBDeliveryTime: And connector text is empty. Using fallback.');
      this.andConnector = 'and';
    }
  }
  initializeDisplay() {
    // Validate DOM structure before attempting to display
    if (!this.validateDOMStructure()) {
      console.error('BBDeliveryTime: Invalid DOM structure, using fallback');
      this.showFallbackDisplay();
      return;
    }

    this.updateDeliveryTimeDisplay();
  }
  startUpdateInterval() {
    // Update every minute
    this.updateInterval = setInterval(() => {
      this.updateDeliveryTimeDisplay();
    }, 60000);

    // Also update immediately on visibility change (when user switches tabs)
    this.handleVisibilityChange = () => {
      if (!document.hidden) {
        this.updateDeliveryTimeDisplay();
      }
    };

    document.addEventListener('visibilitychange', this.handleVisibilityChange);
  }

  clearUpdateInterval() {
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
      this.updateInterval = null;
    }

    if (this.handleVisibilityChange) {
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      this.handleVisibilityChange = null;
    }
  }
  calculateNextThreshold() {
    try {
      const now = new Date();

      // Get current time in Berlin timezone
      const nowInBerlin = new Date(
        now.toLocaleString('en-US', {
          timeZone: this.timeZone,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false,
        }),
      );

      // Validate Berlin time
      if (isNaN(nowInBerlin.getTime())) {
        throw new Error('Invalid Berlin time calculation');
      }

      // Create threshold for today in Berlin time
      const todayThresholdBerlin = new Date(nowInBerlin);
      todayThresholdBerlin.setHours(this.thresholdHour, this.thresholdMinute, 0, 0);

      // Calculate the offset between Berlin time and local time
      const berlinOffset = nowInBerlin.getTime() - now.getTime();

      // Convert Berlin threshold back to local time for countdown calculation
      const todayThresholdLocal = new Date(todayThresholdBerlin.getTime() - berlinOffset);

      // Validate threshold date
      if (isNaN(todayThresholdLocal.getTime())) {
        throw new Error('Invalid threshold time calculation');
      }

      // If current local time is before today's threshold (in local time), use today's threshold
      if (now < todayThresholdLocal) {
        return todayThresholdLocal;
      } else {
        // Otherwise, use next business day's threshold
        const nextBusinessDayBerlin = this.getNextBusinessDay(nowInBerlin);
        nextBusinessDayBerlin.setHours(this.thresholdHour, this.thresholdMinute, 0, 0);

        // Convert back to local time
        const nextThresholdLocal = new Date(nextBusinessDayBerlin.getTime() - berlinOffset);

        if (isNaN(nextThresholdLocal.getTime())) {
          throw new Error('Invalid next business day threshold calculation');
        }

        return nextThresholdLocal;
      }
    } catch (error) {
      console.error('BBDeliveryTime: Error calculating next threshold:', error);

      // Fallback: return a valid date 24 hours from now
      const fallbackDate = new Date();
      fallbackDate.setDate(fallbackDate.getDate() + 1);
      fallbackDate.setHours(14, 0, 0, 0); // Default 14:00 threshold
      return fallbackDate;
    }
  }
  calculateRemainingTime(targetTime) {
    try {
      const now = new Date(); // Use local time since targetTime is already in local time

      // Validate dates
      if (isNaN(now.getTime()) || isNaN(targetTime.getTime())) {
        throw new Error('Invalid date values in remaining time calculation');
      }

      const timeDiff = targetTime - now;

      // If time has passed, show next threshold
      if (timeDiff <= 0) {
        return `1 ${this.minuteSingular}`;
      }

      const hours = Math.floor(timeDiff / (1000 * 60 * 60));
      const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));

      // Validate calculated values
      if (hours < 0 || minutes < 0) {
        throw new Error('Negative time values calculated');
      }

      // Format time string
      return this.formatTimeString(hours, minutes);
    } catch (error) {
      console.error('BBDeliveryTime: Error calculating remaining time:', error);
      // Fallback: return a generic time string
      return `1 ${this.hourSingular}`;
    }
  }

  // Helper method to format time strings consistently
  formatTimeString(hours, minutes) {
    if (hours > 0) {
      const hoursText = hours === 1 ? this.hourSingular : this.hourPlural;
      if (minutes > 0) {
        const minutesText = minutes === 1 ? this.minuteSingular : this.minutePlural;
        return `${hours} ${hoursText} ${this.andConnector} ${minutes} ${minutesText}`;
      } else {
        return `${hours} ${hoursText}`;
      }
    } else {
      const minutesText = minutes === 1 ? this.minuteSingular : this.minutePlural;
      return `${Math.max(1, minutes)} ${minutesText}`;
    }
  }
  getNextBusinessDay(date) {
    try {
      if (isNaN(date.getTime())) {
        throw new Error('Invalid date provided to getNextBusinessDay');
      }

      const nextDay = new Date(date);
      nextDay.setDate(nextDay.getDate() + 1);

      // Prevent infinite loop by limiting iterations
      let iterations = 0;
      const maxIterations = 10; // Should never need more than 7 to skip weekends

      // Skip weekends
      while ((nextDay.getDay() === 0 || nextDay.getDay() === 6) && iterations < maxIterations) {
        nextDay.setDate(nextDay.getDate() + 1);
        iterations++;
      }

      if (iterations >= maxIterations) {
        console.error('BBDeliveryTime: Too many iterations finding next business day');
        // Return Monday if we can't find a business day
        const monday = new Date(date);
        monday.setDate(monday.getDate() + ((8 - monday.getDay()) % 7));
        return monday;
      }

      if (isNaN(nextDay.getTime())) {
        throw new Error('Invalid next business day calculation result');
      }

      return nextDay;
    } catch (error) {
      console.error('BBDeliveryTime: Error calculating next business day:', error);

      // Fallback: return tomorrow
      const fallback = new Date(date);
      fallback.setDate(fallback.getDate() + 1);
      return fallback;
    }
  }
  calculateDeliveryDates() {
    try {
      const now = new Date();

      // Get current time in Berlin timezone for delivery date calculation
      const nowInBerlin = new Date(
        now.toLocaleString('en-US', {
          timeZone: this.timeZone,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false,
        }),
      );

      // Create threshold for today in Berlin time
      const todayThresholdBerlin = new Date(nowInBerlin);
      todayThresholdBerlin.setHours(this.thresholdHour, this.thresholdMinute, 0, 0);

      // Validate dates
      if (isNaN(nowInBerlin.getTime()) || isNaN(todayThresholdBerlin.getTime())) {
        throw new Error('Invalid dates in delivery calculation');
      }

      // Validate delivery days are reasonable
      if (this.minDays < 1 || this.maxDays < 1 || this.minDays > 100 || this.maxDays > 100) {
        throw new Error('Invalid delivery days configuration');
      }

      // Determine if we should use today or tomorrow as the base date (all in Berlin time)
      let baseDate;
      if (nowInBerlin < todayThresholdBerlin) {
        // Current Berlin time is before threshold, so delivery starts from today + min days
        baseDate = new Date(nowInBerlin);
      } else {
        // Current Berlin time is after threshold, so delivery starts from tomorrow + min days
        baseDate = new Date(nowInBerlin);
        baseDate.setDate(baseDate.getDate() + 1);
      }

      if (isNaN(baseDate.getTime())) {
        throw new Error('Invalid base date calculation');
      }

      // Skip weekends for business days calculation
      const minDeliveryDate = this.addBusinessDays(baseDate, this.minDays);
      const maxDeliveryDate = this.addBusinessDays(baseDate, this.maxDays);

      // Validate delivery dates
      if (isNaN(minDeliveryDate.getTime()) || isNaN(maxDeliveryDate.getTime())) {
        throw new Error('Invalid delivery dates calculated');
      }

      return {
        minDate: this.formatDate(minDeliveryDate),
        maxDate: this.formatDate(maxDeliveryDate),
      };
    } catch (error) {
      console.error('BBDeliveryTime: Error calculating delivery dates:', error);

      // Fallback: return generic dates 2-5 days from now
      const today = new Date();
      const minFallback = new Date(today);
      minFallback.setDate(today.getDate() + 2);
      const maxFallback = new Date(today);
      maxFallback.setDate(today.getDate() + 5);

      return {
        minDate: this.formatDate(minFallback),
        maxDate: this.formatDate(maxFallback),
      };
    }
  }
  addBusinessDays(startDate, days) {
    try {
      if (isNaN(startDate.getTime())) {
        throw new Error('Invalid start date provided to addBusinessDays');
      }

      if (days < 0 || days > 100) {
        throw new Error(`Invalid days value: ${days}. Must be between 0 and 100.`);
      }

      const result = new Date(startDate);
      let daysAdded = 0;
      let iterations = 0;
      const maxIterations = days * 2 + 10; // Reasonable upper limit to prevent infinite loops

      while (daysAdded < days && iterations < maxIterations) {
        result.setDate(result.getDate() + 1);
        iterations++;

        // Validate the date is still valid
        if (isNaN(result.getTime())) {
          throw new Error('Invalid date during business days calculation');
        }

        // Skip weekends (0 = Sunday, 6 = Saturday)
        if (result.getDay() !== 0 && result.getDay() !== 6) {
          daysAdded++;
        }
      }

      if (iterations >= maxIterations) {
        console.error('BBDeliveryTime: Too many iterations in addBusinessDays');
        // Fallback: just add the days without business day logic
        const fallback = new Date(startDate);
        fallback.setDate(startDate.getDate() + days);
        return fallback;
      }

      return result;
    } catch (error) {
      console.error('BBDeliveryTime: Error adding business days:', error);

      // Fallback: just add the days without business day logic
      const fallback = new Date(startDate);
      fallback.setDate(startDate.getDate() + days);
      return fallback;
    }
  }

  formatDate(date) {
    try {
      // Format as DD.MM. (German format)
      const day = date.getDate();
      const month = date.getMonth() + 1;
      return `${day}.${month}.`;
    } catch (error) {
      console.error('BBDeliveryTime: Error formatting date:', error);
      return 'N/A';
    }
  }
  updateDeliveryTimeDisplay() {
    try {
      const nextThreshold = this.calculateNextThreshold();
      const remainingTime = this.calculateRemainingTime(nextThreshold);
      const deliveryDates = this.calculateDeliveryDates();

      // Replace placeholders in the locale text
      let displayText = this.localeText
        .replace('{{ remaining_time }}', remainingTime)
        .replace('{{ min_date }}', deliveryDates.minDate)
        .replace('{{ max_date }}', deliveryDates.maxDate);

      // Find the prose div and update it
      const proseDiv = this.querySelector('.prose p');
      if (proseDiv) {
        proseDiv.innerHTML = displayText;
        // Show the block now that we have the proper content
        this.showBlock();
      } else {
        console.warn('BBDeliveryTime: Could not find .prose p element for display update');
      }
    } catch (error) {
      console.error('BBDeliveryTime: Error updating display:', error);
      this.showFallbackDisplay();
    }
  }

  // Add method to validate DOM structure
  validateDOMStructure() {
    const proseDiv = this.querySelector('.prose p');
    if (!proseDiv) {
      console.warn('BBDeliveryTime: Required DOM structure (.prose p) not found');
      return false;
    }
    return true;
  }
}

customElements.define('bb-delivery-time', BBDeliveryTime);

class BBSondermasse extends HTMLElement {
  constructor() {
    super();
    this.variantId = null;
    this.preisProQm = null;
    this.formId = null;
    this.moneyFormat = null;
    this.currencyCodeEnabled = false;
  }

  connectedCallback() {
    try {
      this.parseAttributes();
      this.setupEventListeners();
      this.setupFormValidation();
    } catch (error) {
      console.error('BBSondermasse: Error during initialization:', error);
    }
  }

  parseAttributes() {
    this.variantId = this.getAttribute('data-variant-id');
    this.preisProQm = parseFloat(this.getAttribute('data-preis-pro-qm'));
    this.formId = this.getAttribute('data-form-id');
    this.moneyFormat = this.getAttribute('data-money-format') || '€{{amount}}';
    this.currencyCodeEnabled = this.getAttribute('data-currency-code-enabled') === 'true';

    // Validation
    if (!this.variantId || isNaN(this.preisProQm) || !this.formId) {
      throw new Error('BBSondermasse: Missing required attributes');
    }
  }

  setupEventListeners() {
    const inputs = this.querySelectorAll('input[data-type]');
    inputs.forEach((input) => {
      input.addEventListener('input', () => this.calculatePrice());
      input.addEventListener('blur', (e) => this.snapToConstraints(e.target));
    });
  }

  setupFormValidation() {
    const form = document.getElementById(this.formId);
    if (form) {
      form.addEventListener('submit', this.handleFormSubmit.bind(this));
    }
  }

  calculatePrice() {
    const laenge = this.getInputValue('laenge');
    const breite = this.getInputValue('breite');

    this.hideError();

    if (laenge > 0 && breite > 0 && this.validateRange(laenge, breite)) {
      const sqm = (laenge * breite) / 10000;
      const price = sqm * this.preisProQm;

      this.updatePriceDisplay(laenge, breite, sqm, price);
      this.updateCartProperty(price);
    } else {
      this.hidePriceDisplay();
      this.clearCartProperty();
    }
  }

  snapToConstraints(input) {
    const value = parseFloat(input.value);
    if (isNaN(value)) return;

    const min = parseFloat(input.getAttribute('min'));
    const max = parseFloat(input.getAttribute('max'));
    const step = parseFloat(input.getAttribute('step')) || 1;

    let snappedValue = value;

    if (!isNaN(min) && value < min) {
      snappedValue = min;
    } else if (!isNaN(max) && value > max) {
      snappedValue = max;
    } else {
      snappedValue = Math.round(value / step) * step;
    }

    if (snappedValue !== value) {
      input.value = snappedValue;
      this.calculatePrice();
    }

    this.validateField(input);
  }

  validateField(input) {
    const value = parseFloat(input.value) || 0;

    if (value > 0 && !this.isInRange(input, value)) {
      input.classList.add('is-invalid');
      console.log('add is-invalid to', input);
    } else {
      input.classList.remove('is-invalid');
      console.log('remove is-invalid from', input);
    }

    const laenge = this.getInputValue('laenge');
    const breite = this.getInputValue('breite');

    if (laenge > 0 && breite > 0 && !this.validateRange(laenge, breite)) {
      this.showError();
    } else {
      this.hideError();
    }
  }

  clearFieldErrors() {
    console.log('clearFieldErrors');
    const inputs = this.querySelectorAll('input[data-type]');
    inputs.forEach((input) => input.classList.remove('is-invalid'));
  }

  getInputValue(type) {
    const input = this.querySelector(`input[data-type="${type}"]`);
    return input ? parseFloat(input.value) || 0 : 0;
  }

  validateRange(laenge, breite) {
    const laengeInput = this.querySelector('input[data-type="laenge"]');
    const breiteInput = this.querySelector('input[data-type="breite"]');
    return this.isInRange(laengeInput, laenge) && this.isInRange(breiteInput, breite);
  }

  isInRange(input, value) {
    if (!input) return false;
    const min = parseFloat(input.getAttribute('min'));
    const max = parseFloat(input.getAttribute('max'));
    return (!isNaN(min) ? value >= min : true) && (!isNaN(max) ? value <= max : true);
  }

  updatePriceDisplay(laenge, breite, sqm, price) {
    const display = this.querySelector('[data-sondermasse="price-display"]');
    const amount = this.querySelector('[data-sondermasse="price-amount"]');
    const dimensions = this.querySelector('[data-sondermasse="dimensions"]');
    const meters = this.querySelector('[data-sondermasse="square-meters"]');

    if (display && amount) {
      display.style.display = 'block';
      amount.textContent = this.formatPrice(price);

      if (dimensions) dimensions.textContent = `${breite} cm × ${laenge} cm`;
      if (meters) meters.textContent = ` (${sqm.toFixed(2).replace('.', ',')} m²)`;
    }
  }

  hidePriceDisplay() {
    const display = this.querySelector('[data-sondermasse="price-display"]');
    if (display) display.style.display = 'none';
  }

  updateCartProperty(price) {
    const hiddenInput = this.querySelector('[data-price-property]');
    if (hiddenInput) {
      const cartProperty = [
        {
          variantId: this.variantId,
          quantityPerQuantity: 1,
          overrideAbsolutePrice: Math.round(price * 100) / 100,
        },
      ];
      hiddenInput.value = JSON.stringify(cartProperty);
    }
  }

  clearCartProperty() {
    const hiddenInput = this.querySelector('[data-price-property]');
    if (hiddenInput) hiddenInput.value = '';
  }

  formatPrice(price) {
    try {
      const amount = price.toFixed(2).replace('.', ',');
      const format = this.currencyCodeEnabled ? this.moneyFormat.replace(/\{\{[^}]+\}\}/g, `${amount} EUR`) : this.moneyFormat.replace(/\{\{[^}]+\}\}/g, amount);
      return format;
    } catch (error) {
      console.error('BBSondermasse: Error formatting price:', error);
      const amount = price.toFixed(2).replace('.', ',');
      return this.currencyCodeEnabled ? `€${amount} EUR` : `€${amount}`;
    }
  }

  handleFormSubmit(event) {
    const laenge = this.getInputValue('laenge');
    const breite = this.getInputValue('breite');

    if (laenge > 0 || breite > 0) {
      if (!this.validateRange(laenge, breite) || laenge <= 0 || breite <= 0) {
        event.preventDefault();
        event.stopPropagation();
        this.showError();

        const laengeInput = this.querySelector('input[data-type="laenge"]');
        const breiteInput = this.querySelector('input[data-type="breite"]');

        if (laenge <= 0 || !this.isInRange(laengeInput, laenge)) {
          laengeInput?.focus();
        } else if (breite <= 0 || !this.isInRange(breiteInput, breite)) {
          breiteInput?.focus();
        }

        return false;
      }
    }
    return true;
  }

  showError() {
    const errorDiv = this.querySelector('[data-sondermasse="error"]');
    if (errorDiv) errorDiv.style.display = 'block';
  }

  hideError() {
    const errorDiv = this.querySelector('[data-sondermasse="error"]');
    if (errorDiv) errorDiv.style.display = 'none';
  }
}

customElements.define('bb-sondermasse', BBSondermasse);

// Redesign 2026: Navbar wie benuta – Navbar + schwarze Statusleiste sind EIN Block.
// Oben scrollt die Navbar normal mit (durchsichtig über dem Hero). Erst wenn sie ganz aus dem Bild ist, wird sie fest
// und unsichtbar weiß; beim Hochscrollen gleitet sie fertig umgefärbt samt Statusleiste herein (0,3 s, eine Kurve).
// Erreicht die normale Position wieder den oberen Rand, geht sie nahtlos zurück in den Seitenfluss.
(() => {
  const header = document.querySelector('store-header');
  const holder = header && header.closest('height-observer');
  if (!header || !holder) return;
  const root = document.documentElement;

  // Schwarze Statusleiste: hängt IN der Navbar und fährt mit ihr; wegklickbar für die Sitzung
  const bar = document.querySelector('.shopify-section--announcement-bar .announcement-bar');
  let closed = false;
  try { closed = sessionStorage.getItem('tc-announce-under-closed') === '1'; } catch (e) {}
  if (bar && !closed) {
    const firstItem = bar.querySelector('.announcement-bar__item, p, a');
    // Gibt es das Newsletter-Fenster, wirbt die Leiste dafür und öffnet es per Klick (wie benuta)
    const popup = document.getElementById('newsletter-drawer');
    const text = popup
      ? `<button type="button" class="tc-announce-under__nl" aria-haspopup="dialog"><mark class="tc-mark">−15 %</mark> im Newsletter <span aria-hidden="true">|</span> <u>Jetzt anmelden</u> <span aria-hidden="true">→</span></button>`
      : (firstItem ? firstItem.innerHTML : bar.textContent);
    const under = document.createElement('div');
    under.className = 'tc-announce-under';
    under.innerHTML = `<div class="tc-announce-under__text">${text}</div>
      <button type="button" class="tc-announce-under__close" aria-label="Hinweis schließen">
        <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true"><path d="M1 1l10 10M11 1L1 11" stroke="currentColor" stroke-width="1.4"/></svg>
      </button>`;
    header.appendChild(under);
    under.querySelector('.tc-announce-under__nl')?.addEventListener('click', () => popup.show());
    const setUnderHeight = () => header.style.setProperty('--tc-under-h', `${under.offsetHeight}px`);
    setUnderHeight();
    window.addEventListener('resize', setUnderHeight);
    under.querySelector('.tc-announce-under__close').addEventListener('click', () => {
      under.remove();
      header.style.setProperty('--tc-under-h', '0px');
      try { sessionStorage.setItem('tc-announce-under-closed', '1'); } catch (e) {}
    });
  }

  let fixed = false;
  let shown = false;
  let lastY = window.scrollY;
  let travel = 0;
  let ticking = false;

  const setShown = (value) => {
    shown = value;
    header.classList.toggle('tc-header--hidden', !value);
    root.style.setProperty('--header-is-visible', value ? '1' : '0');
  };

  // Wechsel in den festen Zustand passiert, während die Navbar außerhalb des Bildes ist – ohne Übergang
  const enterFixed = () => {
    holder.style.height = `${holder.offsetHeight}px`;
    header.classList.add('tc-header--instant', 'tc-header--fixed', 'is-filled');
    header.removeAttribute('data-tc-top');
    setShown(false);
    void header.offsetHeight;
    requestAnimationFrame(() => header.classList.remove('tc-header--instant'));
    fixed = true;
  };

  // Zurück in den Seitenfluss genau dann, wenn die normale Position am oberen Rand liegt (kein Sprung)
  const exitFixed = () => {
    header.classList.remove('tc-header--fixed', 'tc-header--hidden');
    header.setAttribute('data-tc-top', '');
    holder.style.height = '';
    root.style.setProperty('--header-is-visible', '1');
    fixed = false;
    travel = 0;
  };

  const update = () => {
    ticking = false;
    const y = Math.max(0, window.scrollY);
    const dy = y - lastY;
    lastY = y;
    const flow = holder.getBoundingClientRect();

    if (!fixed) {
      if (flow.bottom <= 0) enterFixed();
      return;
    }
    if (flow.top >= 0) {
      exitFixed();
      return;
    }
    // Fest = immer weiß; nicht auf die Theme-Klasse verlassen (die setzt sie nur verzögert/gar nicht)
    if (!header.classList.contains('is-filled')) header.classList.add('is-filled');
    if (header.querySelector('[open]')) return;

    // Kleine Toleranz, damit leichtes Zittern beim Scrollen die Navbar nicht auf- und zuklappt
    travel = Math.sign(dy) === Math.sign(travel) ? travel + dy : dy;
    if (travel < -4 && !shown) setShown(true);
    else if (travel > 12 && shown) setShown(false);
  };

  header.setAttribute('data-tc-top', '');
  window.addEventListener('scroll', () => {
    if (!ticking) {
      ticking = true;
      requestAnimationFrame(update);
    }
  }, { passive: true });
  window.addEventListener('resize', update);
  update();
})();

// Redesign 2026: Mega-Menü schneller & starrer (wie benuta) – ohne theme.js zu ändern.
// Kein gestaffeltes Einblenden der einzelnen Links, kurze Blende, Schließverzögerung 150 ms statt 500 ms.
customElements.whenDefined('mega-menu-disclosure').then(() => {
  const MegaMenu = customElements.get('mega-menu-disclosure');
  if (!MegaMenu) return;
  Object.defineProperty(MegaMenu.prototype, 'mouseOverDelayTolerance', { get: () => 150, configurable: true });
  MegaMenu.prototype._transitionIn = function () {
    const el = this.contentElement;
    el.style.transition = 'opacity 0.12s ease';
    el.style.opacity = '1';
    return Promise.resolve();
  };
  MegaMenu.prototype._transitionOut = function () {
    const el = this.contentElement;
    el.style.transition = 'none';
    el.style.opacity = '0';
    return Promise.resolve();
  };
});

// Redesign 2026: SEO-Texte auf Kategorieseiten als „Ratgeber" darstellen.
// Arbeitet nur mit der Struktur (H2-Kapitel, <ol> = Schritte, H2 „FAQ" + H3-Fragen) – der Text bleibt unverändert im DOM.
// Ausfallsicher: Weicht der Aufbau ab (weniger als 2 H2, H2 in verschiedenen Containern) oder tritt ein Fehler auf,
// bleibt der Text genau so stehen, wie er aus Shopify kommt – es geht nie Text verloren.
(() => {
  const section = document.querySelector('.shopify-section[id$="__rich_text_RCHPdb"]');
  const h2s = section ? [...section.querySelectorAll('.prose h2')] : [];
  if (h2s.length < 2) return;
  // Container, in dem die H2 direkt liegen (Metafeld-Ausgabe steckt in einem eigenen Wrapper)
  const prose = h2s[0].parentElement;
  if (h2s.some((h) => h.parentElement !== prose)) return;
  const original = [...prose.childNodes];
  try {
    tcBuildRatgeber(section, prose);
  } catch (err) {
    prose.replaceChildren(...original);
    section.classList.remove('tc-seo-section');
    console.warn('SEO-Ratgeber: Text unverändert angezeigt', err);
  }
})();

function tcBuildRatgeber(section, prose) {
  const slug = (t) => t.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 60);
  const usedIds = new Set();
  const uniqueId = (base) => { let id = base || 'kapitel'; let n = 2; while (usedIds.has(id) || document.getElementById(id)) id = `${base}-${n++}`; usedIds.add(id); return id; };

  // 1. In Kapitel gliedern: jede H2 mit allem bis zur nächsten H2
  const article = document.createElement('div');
  article.className = 'tc-seo__article';
  let chapter = null;
  [...prose.childNodes].forEach((node) => {
    if (node.nodeType === 1 && node.tagName === 'H2') {
      chapter = document.createElement('section');
      chapter.className = 'tc-seo__chapter';
      article.appendChild(chapter);
    } else if (!chapter) {
      chapter = document.createElement('section');
      chapter.className = 'tc-seo__chapter';
      article.appendChild(chapter);
    }
    chapter.appendChild(node);
  });

  const chapters = [...article.querySelectorAll('.tc-seo__chapter')];
  const faqItems = [];
  chapters.forEach((ch, i) => {
    const h2 = ch.querySelector('h2');
    if (!h2) return;
    const title = h2.textContent.trim();
    h2.id = h2.id || uniqueId(`ratgeber-${slug(title)}`);
    if (i === 0) ch.classList.add('tc-seo__chapter--intro');
    if (/kaufen/i.test(title)) ch.classList.add('tc-seo__chapter--cta');
    if (ch.querySelector(':scope > ol')) ch.classList.add('tc-seo__chapter--steps');

    // 2. FAQ: Frage + Antwort bis zur nächsten Frage als aufklappbarer Eintrag
    // Überschrift: „FAQ …“, „Häufige Fragen“, „Häufig gestellte Fragen“, „Fragen und Antworten“, „Fragen & Antworten“
    // Fragen: H3 (Standard der Agentur), sonst H4, sonst Absätze, die nur aus einer fetten Frage bestehen
    if (/^(faq|häufig(e|\s+gestellte)?\s+fragen|fragen\s+(und|&)\s+antworten)/i.test(title)) {
      const isBoldQuestion = (el) => el.tagName === 'P' && el.children.length === 1
        && /^(STRONG|B)$/.test(el.firstElementChild.tagName)
        && el.textContent.trim() === el.firstElementChild.textContent.trim() && /\?\s*$/.test(el.textContent);
      let questions = [...ch.querySelectorAll(':scope > h3')];
      if (!questions.length) questions = [...ch.querySelectorAll(':scope > h4')];
      if (!questions.length) questions = [...ch.children].filter(isBoldQuestion);
      if (questions.length) ch.classList.add('tc-seo__chapter--faq');
      const isQuestion = new Set(questions);
      questions.forEach((q) => {
        const details = document.createElement('details');
        details.className = 'tc-seo__faq';
        const summary = document.createElement('summary');
        const answer = document.createElement('div');
        answer.className = 'tc-seo__faq-answer';
        let next = q.nextSibling;
        while (next && !(next.nodeType === 1 && (next.tagName === 'H2' || isQuestion.has(next)))) {
          const move = next;
          next = next.nextSibling;
          answer.appendChild(move);
        }
        q.replaceWith(details);
        summary.appendChild(q);
        details.append(summary, answer);
        faqItems.push({ q: q.textContent.trim(), a: answer.textContent.replace(/\s+/g, ' ').trim() });
      });
    }
  });

  // 3. Inhaltsverzeichnis aus den H2
  const toc = document.createElement('nav');
  toc.className = 'tc-seo__toc';
  toc.setAttribute('aria-label', 'Inhalt');
  toc.innerHTML = '<p class="tc-seo__toc-title">Inhalt</p><ol></ol>';
  chapters.forEach((ch) => {
    const h2 = ch.querySelector('h2');
    if (!h2) return;
    const li = document.createElement('li');
    const a = document.createElement('a');
    a.href = `#${h2.id}`;
    a.textContent = h2.textContent.trim().split(/:\s/)[0];
    li.appendChild(a);
    toc.querySelector('ol').appendChild(li);
  });

  const wrap = document.createElement('div');
  wrap.className = 'tc-seo';
  wrap.append(toc, article);
  prose.replaceChildren(wrap);
  section.classList.add('tc-seo-section');

  // Aktives Kapitel im Inhaltsverzeichnis markieren
  const links = [...toc.querySelectorAll('a')];
  const io = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      if (!e.isIntersecting) return;
      links.forEach((l) => l.classList.toggle('is-active', l.getAttribute('href') === `#${e.target.id}`));
    });
  }, { rootMargin: '0px 0px -80% 0px' });
  chapters.forEach((ch) => { const h2 = ch.querySelector('h2'); if (h2) io.observe(h2); });

  // 4. FAQ-Schema für Google
  if (faqItems.length) {
    const ld = document.createElement('script');
    ld.type = 'application/ld+json';
    ld.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqItems.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
    });
    document.head.appendChild(ld);
  }
}

// Leere SEO-Sektionen unter der Produktliste ausblenden (Metafelder noch nicht gepflegt)
(() => {
  if (!document.querySelector('.shopify-section--main-collection')) return;
  document.querySelectorAll('.shopify-section--rich-text, .shopify-section--multi-column').forEach((sec) => {
    const hasText = sec.innerText.replace(/\s+/g, '').length > 0; // innerText: ohne eingebettetes CSS
    const hasImg = sec.querySelector('img[src]:not([src=""])');
    if (!hasText && !hasImg) sec.style.display = 'none';
  });
})();

// Redesign 2026: Inhaltsverzeichnis auf Artikelseiten aus den H2 erzeugen (Stil wie SEO-Ratgeber der Kategorien)
(() => {
  const body = document.querySelector('[data-tc-article-body]');
  const holder = document.querySelector('[data-tc-toc]');
  if (!body || !holder) return;
  const heads = [...body.querySelectorAll('h2')];
  if (heads.length < 3) return;
  const slug = (t) => t.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 60);
  const toc = document.createElement('nav');
  toc.className = 'tc-seo__toc';
  toc.setAttribute('aria-label', 'Inhalt');
  toc.innerHTML = '<p class="tc-seo__toc-title">Inhalt</p><ol></ol>';
  heads.forEach((h) => {
    h.id = h.id || `abschnitt-${slug(h.textContent.trim())}`;
    const li = document.createElement('li');
    const a = document.createElement('a');
    a.href = `#${h.id}`;
    a.textContent = h.textContent.trim().split(/:\s/)[0];
    li.appendChild(a);
    toc.querySelector('ol').appendChild(li);
  });
  holder.appendChild(toc);
  holder.classList.add('tc-seo');
  const links = [...toc.querySelectorAll('a')];
  const io = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      if (e.isIntersecting) links.forEach((l) => l.classList.toggle('is-active', l.getAttribute('href') === `#${e.target.id}`));
    });
  }, { rootMargin: '0px 0px -80% 0px' });
  heads.forEach((h) => io.observe(h));
})();

// Menge (Dropdown neben der Größe, snippets/tc-quantity-select.liquid): Anzeige im Knopf aktualisieren und die Wahl
// beim Variantenwechsel behalten – Impact rendert den Varianten-Block dabei neu
(() => {
  const remembered = {};
  const showValue = (input) => {
    const popover = input.closest('x-popover');
    const label = popover && document.querySelector(`[aria-controls="${popover.id}"] [data-tc-qty-value]`);
    if (label) label.textContent = input.value;
  };
  document.addEventListener('change', (event) => {
    if (!event.target.matches?.('input[data-tc-qty]')) return;
    remembered[event.target.getAttribute('form')] = event.target.value;
    showValue(event.target);
  });
  new MutationObserver(() => {
    Object.entries(remembered).forEach(([form, value]) => {
      const input = document.querySelector(`input[data-tc-qty][form="${form}"][value="${value}"]`);
      if (input && !input.checked) {
        input.checked = true;
        showValue(input);
      }
    });
  }).observe(document.body, { childList: true, subtree: true });
})();

// Newsletter-Hinweis in der braunen Ankündigungsleiste: öffnet das Newsletter-Fenster statt der Newsletter-Seite
document.addEventListener('click', (event) => {
  const link = event.target.closest?.('[data-tc-open-newsletter]');
  const popup = document.getElementById('newsletter-drawer');
  if (!link || !popup || typeof popup.show !== 'function') return;
  event.preventDefault();
  popup.show();
});

// Mitscrollende Kaufleiste: „Größe“ bzw. „+N“ springt zur Auswahl auf der Seite und öffnet dort die Größen-Liste
document.addEventListener('click', (event) => {
  const trigger = event.target.closest?.('[data-tc-sticky-size], [data-tc-sticky-scroll]');
  if (!trigger) return;
  const picker = document.querySelector('.product-info .variant-picker');
  if (!picker) return;
  const sizeButton = picker.querySelector('.tc-size-qty .variant-picker__option button.select') || picker.querySelector('.variant-picker__option:last-of-type button.select');
  const target = trigger.hasAttribute('data-tc-sticky-size') && sizeButton ? sizeButton : picker;
  target.scrollIntoView({ behavior: 'smooth', block: 'center' });
  if (target === sizeButton) setTimeout(() => sizeButton.click(), 450);
});

// Judge.me-Bewertungen: Initialen aus Vor- und Nachname im Kreis („Margit Hecker“ → „MH“, „Anonym“ → „A“).
// Nur als data-Attribut (Anzeige per CSS ::before), den Text selbst verwaltet Judge.me – nach jedem Seitenwechsel neu gesetzt.
(() => {
  const initialsOf = (name) => name.trim().split(/\s+/).filter((word) => /^\p{L}/u.test(word)).slice(0, 2).map((word) => word.charAt(0).toUpperCase()).join('');
  const update = () => {
    document.querySelectorAll('#judgeme_product_reviews .jm-reviewer-info').forEach((info) => {
      const name = info.querySelector('.jm-reviewer-info__name');
      const avatar = info.querySelector('.jm-reviewer-avatar__initial');
      if (!name || !avatar) return;
      const initials = initialsOf(name.textContent || '');
      if (initials && avatar.getAttribute('data-tc-initials') !== initials) avatar.setAttribute('data-tc-initials', initials);
    });
  };
  let queued = false;
  new MutationObserver(() => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => { queued = false; update(); });
  }).observe(document.body, { childList: true, subtree: true, characterData: true });
  update();
})();

// Warenkorb: Gutscheincode einlösen/entfernen über die Cart-API (/cart/update.js, Feld „discount“), danach neu laden,
// damit Summen und Rabattzeilen stimmen. Enter im Feld löst ein (statt das Warenkorb-Formular zur Kasse zu schicken).
(() => {
  const updateCodes = async (codes) => {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: codes.join(',') }),
    });
    return response.json();
  };
  const appliedCodes = (root) => [...root.querySelectorAll('[data-tc-discount-code]')].map((el) => el.dataset.tcDiscountCode);
  const showError = (root, text) => {
    const error = root.querySelector('.tc-discount__error');
    error.textContent = text;
    error.hidden = false;
  };

  document.addEventListener('click', async (event) => {
    const root = event.target.closest?.('.tc-discount');
    if (!root) return;
    const submit = event.target.closest('.tc-discount__submit');
    const remove = event.target.closest('.tc-discount__remove');
    if (!submit && !remove) return;
    event.preventDefault();

    if (remove) {
      await updateCodes(appliedCodes(root).filter((code) => code !== remove.dataset.code));
      window.location.reload();
      return;
    }

    const input = root.querySelector('.tc-discount__input');
    const code = input.value.trim();
    if (!code) { input.focus(); return; }
    submit.disabled = true;
    try {
      const cart = await updateCodes([...appliedCodes(root), code]);
      const entry = (cart.discount_codes || []).find((item) => item.code.toLowerCase() === code.toLowerCase());
      if (entry && entry.applicable) { window.location.reload(); return; }
      await updateCodes(appliedCodes(root));
      showError(root, 'Dieser Gutscheincode ist nicht gültig oder gilt nicht für deinen Warenkorb.');
    } catch (error) {
      showError(root, 'Der Gutscheincode konnte gerade nicht geprüft werden. Bitte versuch es noch einmal.');
    }
    submit.disabled = false;
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' || !event.target.matches?.('.tc-discount__input')) return;
    event.preventDefault();
    event.target.closest('.tc-discount').querySelector('.tc-discount__submit').click();
  });

  // 28.09.: Aufklapper „Gutscheincode eingeben“ – beim Öffnen selbst aufklappen und sofort ins Feld (noch im Tipp, sonst öffnet
  // iOS die Tastatur nicht); Zuklappen macht der Browser wie gewohnt
  document.addEventListener('click', (event) => {
    const toggle = event.target.closest?.('.tc-discount__toggle');
    const box = toggle?.closest('details');
    if (!box || box.open) return;
    event.preventDefault();
    box.open = true;
    box.querySelector('.tc-discount__input')?.focus();
  });
})();

// 29.09.2026: Hover-Bild der Produktkarten erst bei Bedarf laden. snippets/product-card.liquid schreibt srcset/src des Zweitbilds nach
// data-tc-srcset/data-tc-src – am Desktop lud es sonst sofort mit (35–40 % der Bild-Bytes einer Kategorieseite, Konkurrenz zu den
// Hauptbildern). Eingesetzt wird, sobald die Maus auf eine Karte kommt (pointerover) oder sie den Tastaturfokus bekommt (focusin);
// bei der ersten Mausbewegung über einem Produktraster einmalig vorab für alle Karten, die gerade im Fenster stehen (erst nach dem
// Laden der Seite). Bis das Bild da ist, trägt .product-card__figure tc-hover-pending: beim Überfahren bleibt das Hauptbild stehen
// statt einer leeren Fläche (custom.css). Touch zählt hier nicht – den Handy-„Hover“ darunter startet touchstart.
function tcLoadCardHover(card) {
  const image = card?.querySelector('.product-card__image--secondary[data-tc-src]');
  if (!image) return;
  const figure = image.closest('.product-card__figure');
  image.addEventListener('load', () => figure?.classList.remove('tc-hover-pending'), { once: true });
  if (image.dataset.tcSrcset) image.srcset = image.dataset.tcSrcset;
  image.src = image.dataset.tcSrc;
  image.removeAttribute('data-tc-srcset');
  image.removeAttribute('data-tc-src');
}

(() => {
  const cardSelector = '.product-card--show-secondary-media';
  const onEnter = (event) => {
    if (event.pointerType === 'touch') return;
    const card = event.target instanceof Element ? event.target.closest(cardSelector) : null;
    if (card) tcLoadCardHover(card);
  };
  document.addEventListener('pointerover', onEnter, { passive: true });
  document.addEventListener('focusin', onEnter);

  const warm = (event) => {
    if (event.pointerType === 'touch') return;
    const list = event.target instanceof Element ? event.target.closest('.product-list') : null;
    if (!list) return;
    document.removeEventListener('pointermove', warm);
    const run = () => list.querySelectorAll(cardSelector).forEach((card) => {
      const rect = card.getBoundingClientRect();
      if (rect.bottom > 0 && rect.top < window.innerHeight && rect.right > 0 && rect.left < window.innerWidth) tcLoadCardHover(card);
    });
    if (document.readyState === 'complete') run();
    else window.addEventListener('load', run, { once: true });
  };
  document.addEventListener('pointermove', warm, { passive: true });
})();

// 29.09.2026: Farbfeld auf der Produktkarte (Maus drüber = Vorladen, Klick = Bildwechsel, theme.js ProductCard._createImageElement)
// – das neue Hauptbild bekommt dieselbe srcset im Kachelformat wie snippets/product-card.liquid (Shopify schneidet mittig zu,
// crop=center = Ausschnitt von object-fit: cover). Ohne das wäre das Bild nach dem Klick wieder unbeschnitten und in der Höhe
// hochgezogen (unscharf). URLs in derselben Parameter-Reihenfolge wie image_url (crop, height, v, width) → dieselben Cache-Einträge.
// Runde Freisteller (tc-card-img--round) und Kacheln ohne festes Format bleiben beim Theme-Verhalten.
// 30.09.2026 (Nachtauftrag Frontbilder): Hat das neue Bild einen gemessenen Rahmen (data-tc-frame am Farbfeld, aus
// snippets/tc-card-frame.liquid), gleiche Rechnung wie snippets/product-card.liquid: Ausschnitt per crop=region + weißer Innenrand.
customElements.whenDefined('product-card').then(() => {
  const proto = customElements.get('product-card')?.prototype;
  if (!proto || typeof proto._createImageElement !== 'function' || proto._tcCropSrcset) return;
  const original = proto._createImageElement;
  const widths = [200, 250, 300, 350, 400, 450, 500, 550, 600, 650, 700, 800, 900, 1000, 1200, 1400, 1600, 1800];
  proto._tcCropSrcset = true;
  proto._createImageElement = function (media, classes, sizes) {
    const image = original.call(this, media, classes, sizes);
    // 30.10.2026: media.alt ist das Connector-Steuerfeld, nach außen nur den Produkttitel zeigen (wie snippets/tc-media-alt.liquid)
    if (image.alt.includes('#')) image.alt = this.querySelector('.product-card__title')?.textContent.trim() || '';
    const preview = media?.preview_image;
    // Klassen kommen vom alten Kartenbild – Rahmen/Rund gelten je Bild, deshalb für das neue Bild neu bestimmen (wie product-card.liquid)
    const hadRound = image.classList.contains('tc-card-img--round');
    image.classList.remove('tc-card-img--framed', 'tc-card-img--round');
    const ratio = /\baspect-tall\b/.test(classes) ? [2, 3] : /\baspect-square\b/.test(classes) ? [1, 1] : /\baspect-short\b/.test(classes) ? [4, 3] : null;
    const [base, query = ''] = (preview?.src || '').split('?');
    const version = new URLSearchParams(query).get('v');
    const frame = ratio?.[0] === 2 && ratio[1] === 3 && version && media.id
      ? this.querySelector(`[data-tc-frame-media="${media.id}"]`)?.getAttribute('data-tc-frame')
      : null;
    if (frame) {
      const [rl, rt, rw, rh, pl, pt, pr, pb] = frame.split(' ').map(Number);
      // links/oben mindestens 1 px wie in product-card.liquid (ohne crop_left/crop_top liefert das CDN das ganze Bild)
      const cropLeft = Math.max(1, Math.floor((rl * preview.width) / 10000));
      const cropTop = Math.max(1, Math.floor((rt * preview.height) / 10000));
      const cropWidth = Math.min(Math.floor((rw * preview.width) / 10000), preview.width - cropLeft);
      const cropHeight = Math.min(Math.floor((rh * preview.height) / 10000), preview.height - cropTop);
      const shareW = 10000 - pl - pr;
      const shareH = 15000 - pt - pb;
      const maxW = Math.floor((cropWidth * 10000) / shareW);
      if (maxW >= 200) {
        const urlFor = (w) => `${base}?crop=region&crop_height=${cropHeight}&crop_left=${cropLeft}&crop_top=${cropTop}&crop_width=${cropWidth}`
          + `&height=${Math.floor((w * shareH) / 10000)}&v=${version}&width=${Math.floor((w * shareW) / 10000)}`;
        const steps = widths.filter((w) => w < maxW);
        if (maxW <= 1800) steps.push(maxW);
        image.srcset = steps.map((w) => `${urlFor(w)} ${w}w`).join(', ');
        image.src = urlFor(maxW);
        image.width = maxW;
        image.height = Math.floor((maxW * 3) / 2);
        image.classList.add('tc-card-img--framed');
        image.style.padding = `${pt / 100}% ${pr / 100}% ${pb / 100}% ${pl / 100}%`;
        return image;
      }
    }
    // Runder Freisteller (Connector-Alt „#rolle_f/#rolle_p … rund“) wird eingepasst statt beschnitten; ohne Alt-Text bleibt es beim alten Kartenbild
    const alt = (media?.alt || '').toLowerCase();
    if (alt ? alt.includes(' rund') && /#rolle_[fp]/.test(alt) : hadRound) {
      image.classList.add('tc-card-img--round');
      return image;
    }
    if (!preview?.src || !ratio) return image;
    const maxW = Math.min(preview.width, Math.floor((preview.height * ratio[0]) / ratio[1]));
    if (!(maxW >= 200) || !version) return image;
    const heightFor = (w) => Math.floor((w * ratio[1]) / ratio[0]);
    const urlFor = (w) => `${base}?crop=center&height=${heightFor(w)}&v=${version}&width=${w}`;
    const steps = widths.filter((w) => w < maxW);
    if (maxW <= 1800) steps.push(maxW);
    image.srcset = steps.map((w) => `${urlFor(w)} ${w}w`).join(', ');
    image.src = urlFor(maxW);
    image.width = maxW;
    image.height = heightFor(maxW);
    return image;
  };
});

// Redesign 2026: „Hover“ fürs Handy (siehe custom.css tc-touch-hover). Berührt der Daumen eine Produktkarte – auch nur zum Scrollen –,
// zeigt sie ab dann ihr zweites Bild; die vorher berührte Karte geht zurück aufs Hauptbild. Per Delegation, damit auch nachgeladene
// Karten (Filter, Seitenwechsel, Karussells) funktionieren.
(() => {
  if (!window.matchMedia('(pointer: coarse)').matches) return;
  let active = null;
  document.addEventListener('touchstart', (event) => {
    const card = event.target instanceof Element ? event.target.closest('.product-card--show-secondary-media') : null;
    if (card === active) return;
    if (active) active.classList.remove('tc-touch-hover');
    active = card;
    if (!card) return;
    const secondary = card.querySelector('.product-card__image--secondary');
    if (!secondary) return;
    card.classList.add('tc-touch-armed');
    // 29.09.2026: Zweitbild erst jetzt anfordern (steht bis dahin in data-tc-srcset, siehe tcLoadCardHover oben)
    tcLoadCardHover(card);
    const show = () => { if (active === card) card.classList.add('tc-touch-hover'); };
    if (secondary.complete && secondary.naturalWidth) show();
    else secondary.addEventListener('load', show, { once: true });
  }, { passive: true });
})();

// Redesign 2026 (Nutzerwunsch 25.09.): Auf Touch-Geräten öffnen Größe und Menge auf der Produktseite die native Auswahl des
// Geräts (iPhone-Auswahlrad) statt der Theme-Liste. Dazu liegt ein unsichtbares <select> über dem Feld; die Wahl wird an die
// Theme-Liste weitergereicht (Label anklicken bzw. Link öffnen) – Varianten-, Preis- und Verfügbarkeitslogik bleiben beim Theme.
// Nach jedem Neuaufbau der Produktinfos (Variantenwechsel) werden die Auswahlen per MutationObserver neu angelegt.
(() => {
  if (!window.matchMedia('(pointer: coarse)').matches) return;
  const build = () => {
    document.querySelectorAll('.product-info button.select[aria-controls]:not([data-tc-native])').forEach((button) => {
      const popover = document.getElementById(button.getAttribute('aria-controls'));
      const options = popover ? [...popover.querySelectorAll('.popover-listbox__option')] : [];
      if (!options.length || !button.parentElement) return;
      button.setAttribute('data-tc-native', '');
      const current = button.querySelector('[id$="-selected-value"]')?.textContent.trim();
      const select = document.createElement('select');
      select.className = 'tc-native-select';
      const title = popover.querySelector('[slot="title"]')?.textContent.trim();
      if (title) select.setAttribute('aria-label', title);
      options.forEach((option, index) => {
        const text = option.textContent.trim().replace(/\s+/g, ' ');
        const el = new Option(option.classList.contains('is-disabled') ? `${text} – ausverkauft` : text, String(index));
        const radio = option.querySelector('input[type="radio"]');
        if ((radio && radio.checked) || (!radio && text === current)) el.selected = true;
        select.add(el);
      });
      select.addEventListener('change', () => {
        const option = options[Number(select.value)];
        if (!option) return;
        if (option.tagName === 'A') window.location.href = option.href;
        else option.click();
      });
      button.parentElement.appendChild(select);
    });
  };
  let queued = false;
  const schedule = () => { if (queued) return; queued = true; requestAnimationFrame(() => { queued = false; build(); }); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', build); else build();
  new MutationObserver(schedule).observe(document.documentElement, { childList: true, subtree: true });
})();

// Redesign 2026 (Nutzerwunsch 25.09.): Unter 1000px keine schwebende Chat-Blase – der Shopify-Chat (<shopify-chat>, Inbox-App)
// öffnet über den Knopf „Chat“ im Burger-Menü. Die Blase ist ein Knopf im (offenen) Shadow-DOM des Chats; wir hängen dort eine
// Regel an, die sie am Handy ausblendet – das Chatfenster selbst bleibt voll funktionsfähig (chat.open()).
(() => {
  const STYLE_ID = 'tc-chat-mobile';
  const patch = (chat) => {
    const root = chat.shadowRoot;
    if (!root || root.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = '@media (max-width: 999px) { .activator { display: none !important; } }';
    root.appendChild(style);
  };
  const init = () => {
    const chat = document.querySelector('shopify-chat');
    document.querySelectorAll('[data-tc-open-chat]').forEach((button) => { button.hidden = !chat; });
    if (!chat) return false;
    patch(chat);
    if (chat.shadowRoot && !chat.dataset.tcObserved) {
      chat.dataset.tcObserved = '1';
      new MutationObserver(() => patch(chat)).observe(chat.shadowRoot, { childList: true });
    }
    return true;
  };
  // Der Chat wird von der App nachgeladen – kurz nachsehen, bis er da ist (max. 30 s)
  let tries = 0;
  const timer = setInterval(() => { if (init() || ++tries > 60) clearInterval(timer); }, 500);
  init();
  document.addEventListener('click', (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-tc-open-chat]') : null;
    const chat = document.querySelector('shopify-chat');
    if (!button || !chat) return;
    // Erst das Menü schließen (bei offener Schublade ist der Chat per CSS versteckt), dann den Chat öffnen
    const drawer = button.closest('navigation-drawer');
    if (drawer && typeof drawer.hide === 'function') drawer.hide();
    setTimeout(() => (typeof chat.open === 'function' ? chat.open() : chat.show?.()), drawer ? 400 : 0);
  });
})();

// Redesign 2026 (Nutzerwunsch 28.09., nach snocks.com): Handy-Menü (snippets/navigation-panel.liquid → .tc-nav, Aussehen custom.css)
// – schiebt als Vollbild-Ebene in 0,3 s von links herein und genauso zurück (vorher Blende/Blatt von unten mit Pause dazwischen),
// – Unterpunkte schieben von rechts darüber („‹ Zurück“), die dritte Ebene klappt als Akkordeon auf,
// – Antippen der Suche blendet statt der Navigation die Vorschläge bzw. Live-Ergebnisse ein, „Abbrechen“ führt zurück.
(() => {
  const SPEED = 300; // ms – gleich --tc-nav-speed in custom.css
  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  const navOf = (drawer) => drawer.querySelector('[data-tc-nav]');

  const setClear = (nav) => {
    const input = nav.querySelector('.tc-nav__input');
    const clear = nav.querySelector('[data-tc-nav-clear]');
    if (input && clear) clear.hidden = input.value === '';
  };

  const exitSearch = (nav) => {
    const input = nav.querySelector('.tc-nav__input');
    const search = nav.querySelector('tc-predictive-search');
    nav.classList.remove('tc-nav--search');
    // Die Theme-Suche setzt den Fokus nach dem Leeren (verzögert) wieder ins Feld – das darf die Suche nicht erneut öffnen
    nav.tcIgnoreFocusUntil = Date.now() + 600;
    if (input) { input.value = ''; input.blur(); }
    setClear(nav);
    if (search) { search._abortController?.abort(); search._transitionToSlot?.('idle'); }
  };

  const resetNav = (nav) => {
    exitSearch(nav);
    nav.querySelectorAll('.tc-nav__panel.is-active').forEach((panel) => panel.classList.remove('is-active'));
    nav.querySelectorAll('[data-tc-nav-open], [data-tc-nav-acc]').forEach((button) => button.setAttribute('aria-expanded', 'false'));
    nav.querySelectorAll('.tc-nav__main, .tc-nav__panel-scroll, .tc-nav__results').forEach((area) => { area.scrollTop = 0; });
  };

  // Ein-/Ausblenden der Schublade: eigene Bewegung statt der Theme-Animation (nur für das Handy-Menü, andere Schubladen unverändert)
  customElements.whenDefined('navigation-drawer').then(() => {
    const proto = customElements.get('navigation-drawer').prototype;
    const { _showTransition: show, _hideTransition: hide, reinitializeDrawer: reinit } = proto;
    proto._showTransition = function (animate = true) {
      if (!navOf(this)) return show.call(this, animate);
      this.style.visibility = 'visible';
      this.classList.toggle('tc-nav-drawer--instant', !animate);
      void this.offsetWidth; // Startlage (links außerhalb) festhalten, sonst steht die Ebene ohne Bewegung sofort da
      this.classList.add('tc-nav-drawer--open');
      return wait(animate ? SPEED : 0).then(() => this.classList.remove('tc-nav-drawer--instant'));
    };
    proto._hideTransition = function () {
      if (!navOf(this)) return hide.call(this);
      this.classList.remove('tc-nav-drawer--open');
      return wait(SPEED).then(() => { if (!this.open) this.style.visibility = ''; });
    };
    proto.reinitializeDrawer = function () {
      const nav = navOf(this);
      return nav ? resetNav(nav) : reinit.call(this);
    };
  });

  // Suche im Menü: eigenes Element auf Basis der Theme-Suche. Die Schublade wird beim Öffnen an <body> gehängt und beim
  // Schließen zurück – die Theme-Suche würde dabei jedes Mal ihre Eingabe-Listener erneut anmelden (2, 4, 6 … Anfragen pro
  // Tastendruck). Das Element meldet sich nur beim ersten Einhängen an.
  customElements.whenDefined('predictive-search').then(() => {
    if (customElements.get('tc-predictive-search')) return;
    const PredictiveSearch = customElements.get('predictive-search');
    customElements.define('tc-predictive-search', class extends PredictiveSearch {
      connectedCallback() {
        if (this.tcConnected) return;
        this.tcConnected = true;
        super.connectedCallback();
      }
    });
  });

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const nav = target?.closest('[data-tc-nav]');
    if (!nav) return;

    const opener = target.closest('[data-tc-nav-open]');
    if (opener) {
      const panel = document.getElementById(opener.getAttribute('aria-controls'));
      if (!panel) return;
      panel.querySelector('.tc-nav__panel-scroll').scrollTop = 0;
      panel.classList.add('is-active');
      opener.setAttribute('aria-expanded', 'true');
      if (opener.matches(':focus-visible')) panel.querySelector('[data-tc-nav-back]')?.focus({ preventScroll: true });
      return;
    }

    const back = target.closest('[data-tc-nav-back]');
    if (back) {
      const panel = back.closest('.tc-nav__panel');
      const owner = nav.querySelector(`[aria-controls="${panel.id}"]`);
      panel.classList.remove('is-active');
      owner?.setAttribute('aria-expanded', 'false');
      if (back.matches(':focus-visible')) owner?.focus({ preventScroll: true });
      return;
    }

    const accordion = target.closest('[data-tc-nav-acc]');
    if (accordion) {
      accordion.setAttribute('aria-expanded', accordion.getAttribute('aria-expanded') === 'true' ? 'false' : 'true');
      return;
    }

    if (target.closest('[data-tc-nav-cancel]')) {
      exitSearch(nav);
      return;
    }

    if (target.closest('[data-tc-nav-clear]')) {
      const input = nav.querySelector('.tc-nav__input');
      input.value = '';
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.focus();
    }
  });

  // Antippen/Tippen im Suchfeld → Suchansicht; leeres Feld zeigt die Vorschläge, der Theme-Code lädt die Live-Ergebnisse
  document.addEventListener('focusin', (event) => {
    const input = event.target instanceof Element && event.target.matches('.tc-nav__input') ? event.target : null;
    if (!input) return;
    const nav = input.closest('[data-tc-nav]');
    if (Date.now() < (nav.tcIgnoreFocusUntil || 0)) { input.blur(); return; }
    nav.classList.add('tc-nav--search');
  });

  document.addEventListener('input', (event) => {
    const input = event.target instanceof Element && event.target.matches('.tc-nav__input') ? event.target : null;
    if (!input) return;
    const nav = input.closest('[data-tc-nav]');
    nav.classList.add('tc-nav--search');
    setClear(nav);
  });

  // Beim Wischen durch die Ergebnisse die Tastatur einklappen (mehr Platz für die Kacheln), die Suche bleibt offen
  document.addEventListener('touchmove', (event) => {
    const active = document.activeElement;
    if (active?.matches?.('.tc-nav__input') && event.target instanceof Element && event.target.closest('.tc-nav__results')) active.blur();
  }, { passive: true });
})();

// Redesign 2026 (Nutzerwunsch 28.09.): Express-Knöpfe im Warenkorb (.tc-express) nur PayPal – Shop Pay, Google Pay und (seit 28.09.
// nachmittags) Apple Pay raus. Shopify übergibt die Knöpfe als Liste im Attribut „wallet-configs“ (Reihenfolge shop_pay, apple_pay,
// paypal, google_pay); wir setzen ein neues Element mit gekürzter Liste ein. Im Checkout selbst bleiben alle Zahlarten.
// Kein PayPal in der Liste oder unbekanntes Format → Express-Bereich ausblenden (data-tc-wallets="keine"), „Zur Kasse“ bleibt.
(() => {
  const KEEP = ['paypal'];
  const tidy = () => {
    document.querySelectorAll('.tc-express shopify-accelerated-checkout-cart:not([data-tc-wallets])').forEach((el) => {
      try {
        const wallets = JSON.parse(el.getAttribute('wallet-configs') || '[]');
        const kept = KEEP.map((name) => wallets.find((wallet) => wallet.name === name)).filter(Boolean);
        if (!kept.length) { el.setAttribute('data-tc-wallets', 'keine'); return; }
        const fresh = document.createElement('shopify-accelerated-checkout-cart');
        [...el.attributes].forEach((attr) => fresh.setAttribute(attr.name, attr.value));
        fresh.setAttribute('wallet-configs', JSON.stringify(kept));
        fresh.setAttribute('data-tc-wallets', kept.map((wallet) => wallet.name).join(' '));
        el.replaceWith(fresh);
      } catch (error) {
        el.setAttribute('data-tc-wallets', 'keine');
      }
    });
  };
  tidy();
  // Warenkorb-Seite und Schublade werden bei Mengenänderungen neu gerendert → neue Knopf-Elemente ebenfalls kürzen
  new MutationObserver(tidy).observe(document.body, { childList: true, subtree: true });
})();

